import "./instrument";
import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./app/App";
import reportWebVitals from "./reportWebVitals";
import "./output.css";
import "./tailwind-utilities.css";
import { initI18n } from "./components/i18n";
import * as Sentry from "@sentry/react";

const renderFatalErrorOverlay = (title: string, bodyHtml: string) => {
  const body = document.body;
  if (!body) return;

  const existing = document.getElementById("fatal-error-overlay");
  const container = existing ?? document.createElement("div");
  container.id = "fatal-error-overlay";
  container.style.cssText = [
    "position:fixed",
    "inset:0",
    "background:black",
    "color:white",
    "padding:1rem",
    "font-family:monospace",
    "z-index:99999",
    "overflow:auto",
  ].join(";");
  container.innerHTML = `<h2>${title}</h2>${bodyHtml}`;

  if (!existing) body.appendChild(container);
};

// Prefer addEventListener over overwriting window.onerror
window.addEventListener("error", (event) => {
  const err = (event as ErrorEvent).error as Error | undefined;
  const escapedStack = err?.stack || "N/A";

  renderFatalErrorOverlay(
    "💥 JavaScript Error",
    [
      `<p><strong>Message:</strong> ${(event as ErrorEvent).message ?? "N/A"}</p>`,
      `<p><strong>Source:</strong> ${(event as ErrorEvent).filename ?? "N/A"}</p>`,
      `<p><strong>Line:</strong> ${(event as ErrorEvent).lineno ?? "N/A"}:${(event as ErrorEvent).colno ?? "N/A"}</p>`,
      `<pre style="white-space:pre-wrap;"><strong>Stack:</strong>\n${escapedStack}</pre>`,
    ].join("")
  );
});

const getRejectionMessage = (reason: unknown): string => {
  if (reason instanceof Error) {
    return reason.stack || reason.message;
  }
  if (typeof reason === "string") {
    return reason;
  }
  try {
    return JSON.stringify(reason);
  } catch {
    return String(reason);
  }
};

const isBenignUnhandledRejection = (reason: unknown): boolean => {
  const message = getRejectionMessage(reason);
  return (
    message.includes("messaging/unsupported-browser") ||
    message.includes("messaging/failed-service-worker") ||
    message.includes("failed-service-worker-registration")
  );
};

// Catch unhandled promise rejections too
window.addEventListener("unhandledrejection", (event) => {
  const reason = (event as PromiseRejectionEvent).reason;

  if (isBenignUnhandledRejection(reason)) {
    console.warn("Ignored benign unhandled rejection:", getRejectionMessage(reason));
    event.preventDefault();
    return;
  }

  const escapedReason = getRejectionMessage(reason);

  renderFatalErrorOverlay(
    "💥 Unhandled Promise Rejection",
    `<pre style="white-space:pre-wrap;"><strong>Reason:</strong>\n${escapedReason}</pre>`
  );
});

// ✅ Ensure root exists
const container = document.getElementById("root");
if (!container) {
  throw new Error("Root element #root not found");
}

const root = ReactDOM.createRoot(container, {
  onUncaughtError: Sentry.reactErrorHandler(),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
});

// Add timeout to i18n initialization to prevent hanging
Promise.race([
  initI18n(),
  new Promise<void>((resolve) => {
    // 3 second timeout for i18n initialization
    setTimeout(() => {
      console.warn("⚠️ i18n initialization timeout, proceeding anyway");
      resolve(undefined);
    }, 3000);
  }),
])
  .then(() => {
    // Note: try/catch here only catches synchronous errors during initial render call.
    // Runtime React errors are captured by Sentry.ErrorBoundary.
    root.render(
      <Sentry.ErrorBoundary fallback={<div>An error occurred</div>}>
        <App />
      </Sentry.ErrorBoundary>
    );
  })
  .catch((err) => {
    console.error("❌ i18n initialization failed:", err);

    const errorMessage =
      err instanceof Error ? err.message : JSON.stringify(err);

    renderFatalErrorOverlay(
      "💥 i18n Init Error",
      `<pre style="white-space:pre-wrap;color:red;">${errorMessage}</pre>`
    );
  });

// Not calling serviceWorkerRegistration.register(): it targets
// /service-worker.js, which has no corresponding build output anywhere in
// this project (no workbox config, no public/service-worker.js) — it 404s
// on every production load and always fails installation. Re-enable once an
// actual service worker is built; see lib/serviceWorkerRegistration.js.

// ✅ Keep performance monitoring (optional)
reportWebVitals();

// ✅ Prefetch top mood images into Cache Storage on first visit
try {
  const urlsToPrefetch = [
    "/colorGirl/anxious.png",
    "/colorGirl/flow.png",
    "/colorGirl/apathic.png",
    "/colorGirl/confident.png",
  ];

  if ("caches" in window) {
    const warmCache = async () => {
      try {
        const cache = await caches.open("mood-images-v1");
        await cache.addAll(urlsToPrefetch);
      } catch {
        // Ignore prefetch errors; app still works
      }
    };

    // Kick off after load so it doesn't block first paint
    if (document.readyState === "complete") {
      void warmCache();
    } else {
      window.addEventListener("load", () => void warmCache(), { once: true });
    }
  }
} catch {
  // Ignore; app still works
}
